import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { queryConfigs } from "@/lib/queryClient";

interface OrganizationMeetingTypeRow {
  id: string;
  org_id: string;
  key: string;
  label: string;
  enabled: boolean;
  sort_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

/** Tipos de reunião padrão (system). Espelha o seed da migration 20260703000000. */
const BUILTIN_MEETING_TYPE_SEED = [
  { key: "qualificacao", label: "Qualificação", sort_order: 10 },
  { key: "vendas", label: "Vendas", sort_order: 20 },
  { key: "pos_vendas", label: "Pós-vendas", sort_order: 30 },
] as const;

const BUILTIN_MEETING_TYPE_KEYS = BUILTIN_MEETING_TYPE_SEED.map((s) => s.key);

const qk = (orgId: string | null) => ["organization-meeting-types", orgId] as const;

/** Referência estável — evita novo array a cada render (loops em useEffect). */
const EMPTY_ROWS: OrganizationMeetingTypeRow[] = [];

export function useOrganizationMeetingTypes() {
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Config estrutural (staleTime 30 min, sem refetch em mount/focus): tabela de
    // config lida em modais de atividade/reunião e relatórios; antes usava o default
    // CRM (30 s) e refetchava ~94 k vezes. As mutations abaixo invalidam a queryKey,
    // então edições aparecem na hora. Ver docs/ai — Chip R2-A (cache de config).
    ...queryConfigs.structural,
    queryKey: qk(orgId),
    queryFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const { data, error } = await supabase
        .from("organization_meeting_types")
        .select("*")
        .eq("org_id", orgId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as OrganizationMeetingTypeRow[];
    },
    enabled: !!orgId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk(orgId) });

  const createCustom = useMutation({
    mutationFn: async (input: { label: string }) => {
      if (!orgId) throw new Error("Sem organização");
      const key = `custom_${crypto.randomUUID()}`;
      const rows = query.data || [];
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0);
      const { data, error } = await supabase
        .from("organization_meeting_types")
        .insert({
          org_id: orgId,
          key,
          label: input.label.trim(),
          enabled: true,
          sort_order: maxOrder + 10,
          is_system: false,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as OrganizationMeetingTypeRow;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Tipo de reunião criado" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível criar o tipo de reunião.", variant: "destructive" });
    },
  });

  const updateRow = useMutation({
    mutationFn: async (input: { id: string; label?: string; enabled?: boolean; sort_order?: number }) => {
      if (!orgId) throw new Error("Sem organização");
      const { id, ...rest } = input;
      const patch: Record<string, unknown> = {};
      for (const k of ["label", "enabled", "sort_order"] as const) {
        if (k in rest && (rest as Record<string, unknown>)[k] !== undefined) {
          patch[k] = (rest as Record<string, unknown>)[k];
        }
      }
      const { error } = await supabase
        .from("organization_meeting_types")
        .update(patch)
        .eq("id", id)
        .eq("org_id", orgId);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível atualizar.", variant: "destructive" });
    },
  });

  const removeCustom = useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error("Sem organização");
      const row = (query.data || []).find((r) => r.id === id);
      if (row?.is_system) throw new Error("Tipo do sistema não pode ser excluído");
      const { error } = await supabase
        .from("organization_meeting_types")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
        .eq("is_system", false);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Tipo de reunião removido" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível excluir o tipo de reunião.", variant: "destructive" });
    },
  });

  const reorder = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      if (!orgId) throw new Error("Sem organização");
      for (let i = 0; i < orderedIds.length; i++) {
        const { error } = await supabase
          .from("organization_meeting_types")
          .update({ sort_order: (i + 1) * 10 })
          .eq("id", orderedIds[i])
          .eq("org_id", orgId);
        if (error) throw error;
      }
    },
    onSuccess: () => invalidate(),
    onError: () => {
      toast({ title: "Erro", description: "Não foi possível reordenar.", variant: "destructive" });
    },
  });

  /** Insere as system rows padrão que faltarem (org antiga sem seed). Requer admin (RLS). */
  const seedMissingBuiltins = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const rows = (queryClient.getQueryData(qk(orgId)) as OrganizationMeetingTypeRow[] | undefined) || [];
      const have = new Set(rows.map((r) => r.key));
      const missing = BUILTIN_MEETING_TYPE_SEED.filter((s) => !have.has(s.key));
      if (missing.length === 0) return;
      const inserts = missing.map((s) => ({
        org_id: orgId,
        key: s.key,
        label: s.label,
        enabled: true,
        sort_order: s.sort_order,
        is_system: true,
      }));
      const { error } = await supabase.from("organization_meeting_types").insert(inserts);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });

  return {
    rows: query.data ?? EMPTY_ROWS,
    isLoading: query.isLoading,
    refetch: query.refetch,
    createCustom: createCustom.mutateAsync,
    updateRow: updateRow.mutateAsync,
    removeCustom: removeCustom.mutateAsync,
    reorder: reorder.mutateAsync,
    seedMissingBuiltins: seedMissingBuiltins.mutateAsync,
    hasAllBuiltins: BUILTIN_MEETING_TYPE_KEYS.every((k) => (query.data ?? []).some((r) => r.key === k)),
    isMutating:
      createCustom.isPending ||
      updateRow.isPending ||
      removeCustom.isPending ||
      reorder.isPending ||
      seedMissingBuiltins.isPending,
  };
}
